import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import { BudgetAggregateRow } from '../../config';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';

interface ClientOption {
  id: number;
  company_name: string;
}

interface RwdClientsSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  rwdRows: BudgetAggregateRow[];
  /** Full refetch — used after add / remove since the row list changes. */
  onListChanged: () => void;
  /** Optimistic local reorder — avoids a refetch (which would scroll-reset
   * the underlying Budget page). Backend is still persisted via the reorder
   * RPC; this just shuffles the cached array to match. */
  onLocalReorder: (orderedIds: number[]) => void;
}

/**
 * Settings modal for managing the RWD-by-client aggregate rows. Lets the user
 * add a new client, reorder via up/down arrows, and delete an existing row
 * with an inline confirm. Kept out of the table to reduce risk of accidental
 * deletion — these are set up once and rarely changed.
 *
 * The delete confirmation lives inline inside this modal (not as a stacked
 * Modal) so the outer modal's click-outside detector doesn't fire when the
 * user clicks Cancel inside the (otherwise portaled) inner modal.
 */
const RwdClientsSettingsModal: React.FC<RwdClientsSettingsModalProps> = ({
  isOpen,
  onClose,
  rwdRows,
  onListChanged,
  onLocalReorder,
}) => {
  const supabase = useSupabaseClient();

  const [localOrder, setLocalOrder] = useState<BudgetAggregateRow[]>([]);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<ClientOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalOrder(rwdRows);
      setConfirmingId(null);
      setAddOpen(false);
      setSearch('');
      setError(null);
    }
  }, [isOpen, rwdRows]);

  const orderChanged = useMemo(() => {
    if (localOrder.length !== rwdRows.length) return false;
    return localOrder.some((r, i) => r.id !== rwdRows[i]?.id);
  }, [localOrder, rwdRows]);

  useEffect(() => {
    if (!addOpen) return;
    let cancelled = false;
    setLoadingOptions(true);
    (async () => {
      const { data, error: rpcError } = await supabase.rpc('search_rwd_clients', {
        p_search: search || null,
        p_limit: 50,
      });
      if (cancelled) return;
      setLoadingOptions(false);
      if (rpcError) {
        console.error('search_rwd_clients failed', rpcError);
        setError(rpcError.message);
        return;
      }
      setOptions((data ?? []) as ClientOption[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, addOpen, search]);

  const move = useCallback((index: number, direction: -1 | 1) => {
    setLocalOrder((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const saveOrder = useCallback(async () => {
    setSavingOrder(true);
    setError(null);
    const orderedIds = localOrder.map((r) => r.id);
    const { error: rpcError } = await supabase.rpc('reorder_budget_aggregate_rows', {
      p_ids: orderedIds,
    });
    setSavingOrder(false);
    if (rpcError) {
      console.error('reorder_budget_aggregate_rows failed', rpcError);
      notifyReadOnly(rpcError);
      setError(rpcError.message);
      return;
    }
    // Optimistic: tell the parent to reorder its cached aggregate_rows array
    // instead of refetching. Refetch would cause the page to scroll-reset.
    onLocalReorder(orderedIds);
  }, [supabase, localOrder, onLocalReorder]);

  const confirmDelete = useCallback(async () => {
    if (confirmingId == null) return;
    setRemoving(true);
    setError(null);
    const { error: rpcError } = await supabase.rpc('remove_budget_aggregate_row', {
      p_id: confirmingId,
    });
    setRemoving(false);
    if (rpcError) {
      console.error('remove_budget_aggregate_row failed', rpcError);
      notifyReadOnly(rpcError);
      setError(rpcError.message);
      return;
    }
    setConfirmingId(null);
    onListChanged();
  }, [supabase, confirmingId, onListChanged]);

  const handlePick = useCallback(
    async (client: ClientOption) => {
      setAdding(true);
      setError(null);
      const { error: rpcError } = await supabase.rpc('add_budget_aggregate_row', {
        p_row_type: 'rwd_client',
        p_client_id: client.id,
      });
      setAdding(false);
      if (rpcError) {
        console.error('add_budget_aggregate_row failed', rpcError);
        notifyReadOnly(rpcError);
        setError(rpcError.message);
        return;
      }
      setAddOpen(false);
      setSearch('');
      onListChanged();
    },
    [supabase, onListChanged],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="RWD client rows"
      size="md"
      hasFooter
      footerContent={
        <div className="rwd-settings__footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {orderChanged && (
            <button
              type="button"
              className="btn-primary"
              onClick={saveOrder}
              disabled={savingOrder}
            >
              {savingOrder ? 'Saving…' : 'Save order'}
            </button>
          )}
        </div>
      }
    >
      <div className="rwd-settings">
        <p className="rwd-settings__hint">
          Manage which RWD clients appear as their own aggregate row on the
          Budget page. Changes affect everyone.
        </p>

        {error && <div className="rwd-settings__error">{error}</div>}

        {localOrder.length === 0 ? (
          <div className="rwd-settings__empty">No RWD clients added yet.</div>
        ) : (
          <ul className="rwd-settings__list">
            {localOrder.map((row, idx) => {
              const isConfirming = confirmingId === row.id;
              return (
                <li key={row.id} className="rwd-settings__item">
                  <div className="rwd-settings__reorder">
                    <button
                      type="button"
                      className="rwd-settings__reorder-btn"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0 || isConfirming}
                      title="Move up"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="rwd-settings__reorder-btn"
                      onClick={() => move(idx, 1)}
                      disabled={idx === localOrder.length - 1 || isConfirming}
                      title="Move down"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                  </div>
                  <span className="rwd-settings__label">{row.label}</span>
                  {isConfirming ? (
                    <div className="rwd-settings__confirm-inline">
                      <span className="rwd-settings__confirm-text-inline">
                        Remove?
                      </span>
                      <button
                        type="button"
                        className="btn-secondary rwd-settings__confirm-btn"
                        onClick={() => setConfirmingId(null)}
                        disabled={removing}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-danger rwd-settings__confirm-btn"
                        onClick={confirmDelete}
                        disabled={removing}
                      >
                        {removing ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="rwd-settings__delete"
                      onClick={() => setConfirmingId(row.id)}
                      title="Remove this client"
                      aria-label={`Remove ${row.label}`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="rwd-settings__add">
          {!addOpen ? (
            <button
              type="button"
              className="rwd-settings__add-trigger"
              onClick={() => setAddOpen(true)}
            >
              + Add RWD client
            </button>
          ) : (
            <div className="rwd-settings__picker">
              <input
                type="text"
                className="rwd-settings__search"
                placeholder="Search RWD clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={adding}
                autoFocus
              />
              <button
                type="button"
                className="rwd-settings__picker-cancel"
                onClick={() => {
                  setAddOpen(false);
                  setSearch('');
                }}
                disabled={adding}
              >
                Cancel
              </button>
              <div className="rwd-settings__picker-results">
                {loadingOptions && (
                  <div className="rwd-settings__picker-hint">Loading…</div>
                )}
                {!loadingOptions && options.length === 0 && (
                  <div className="rwd-settings__picker-hint">
                    {search ? 'No matching RWD clients.' : 'No RWD clients left to add.'}
                  </div>
                )}
                {!loadingOptions &&
                  options.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="rwd-settings__picker-option"
                      onClick={() => handlePick(c)}
                      disabled={adding}
                    >
                      {c.company_name}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RwdClientsSettingsModal;
